/**
 * Service API pour la base de connaissances
 */

import type {
  KBCategory,
  KBArticle,
  ArticleFilters,
  CreateCategoryDto,
  UpdateCategoryDto,
  CreateArticleDto,
  UpdateArticleDto,
  KBStats,
  PaginatedArticles,
} from '../types/kb.types';
import { fetchWithAuth, safeJsonParse, API_URL } from '@/utils/api';

// User-facing endpoints (read-only, published articles)
const API_BASE = `${API_URL}/knowledge-base`;
// Admin endpoints (CRUD — superadmin only)
const ADMIN_API_BASE = `${API_URL}/superadmin/knowledge-base`;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await safeJsonParse(response).catch(() => ({ message: 'Erreur serveur' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  const json = await safeJsonParse(response);
  return json.data;
}

export const kbAPI = {
  // ============ LIST & GET BY ID (for HelpCenterPage) ============

  async list(
    filters: { search?: string; category?: string; status?: string; page?: number; limit?: number } = {}
  ): Promise<{ data: KBArticle[]; meta?: any }> {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.status) params.set('status', filters.status);

    const response = await fetchWithAuth(`${API_BASE}/articles?${params.toString()}`);

    const json = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(json.message || 'Erreur lors du chargement des articles');
    }

    return {
      data: json.data || [],
      meta: json.meta,
    };
  },

  async getById(id: string): Promise<KBArticle> {
    const response = await fetchWithAuth(`${API_BASE}/articles/${id}`);
    return handleResponse<KBArticle>(response);
  },

  // ============ CATEGORIES ============

  async getCategories(): Promise<KBCategory[]> {
    const response = await fetchWithAuth(`${API_BASE}/categories`);
    return handleResponse<KBCategory[]>(response);
  },

  async getCategoryById(id: string): Promise<KBCategory & { articles: KBArticle[] }> {
    const response = await fetchWithAuth(`${API_BASE}/categories/${id}`);
    return handleResponse(response);
  },

  async createCategory(data: CreateCategoryDto): Promise<KBCategory> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<KBCategory>(response);
  },

  async updateCategory(id: string, data: UpdateCategoryDto): Promise<KBCategory> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<KBCategory>(response);
  },

  async deleteCategory(id: string): Promise<void> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/categories/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la suppression');
    }
  },

  async reorderCategories(
    reorderData: Array<{ id: string; sortOrder: number }>
  ): Promise<void> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/categories/reorder`, {
      method: 'POST',
      body: JSON.stringify({ categories: reorderData }),
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la réorganisation');
    }
  },

  // ============ ARTICLES ============

  async getArticles(
    filters: ArticleFilters & { page?: number; limit?: number } = {}
  ): Promise<PaginatedArticles> {
    const params = new URLSearchParams();

    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.search) params.set('search', filters.search);
    if (filters.categoryId) params.set('categoryId', filters.categoryId);
    if (filters.isPublished !== undefined) params.set('isPublished', String(filters.isPublished));

    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles?${params.toString()}`);

    const json = await safeJsonParse(response);
    if (!response.ok) {
      throw new Error(json.message || 'Erreur lors du chargement des articles');
    }

    return {
      articles: json.data,
      pagination: json.meta,
    };
  },

  async getArticleById(id: string): Promise<KBArticle> {
    const response = await fetchWithAuth(`${API_BASE}/articles/${id}`);
    return handleResponse<KBArticle>(response);
  },

  async createArticle(data: CreateArticleDto): Promise<KBArticle> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse<KBArticle>(response);
  },

  async updateArticle(id: string, data: UpdateArticleDto): Promise<KBArticle> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return handleResponse<KBArticle>(response);
  },

  async deleteArticle(id: string): Promise<void> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Erreur lors de la suppression');
    }
  },

  async publishArticle(id: string): Promise<KBArticle> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles/${id}/publish`, {
      method: 'POST',
    });
    return handleResponse<KBArticle>(response);
  },

  async unpublishArticle(id: string): Promise<KBArticle> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/articles/${id}/unpublish`, {
      method: 'POST',
    });
    return handleResponse<KBArticle>(response);
  },

  async searchArticles(query: string, limit: number = 20): Promise<KBArticle[]> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const response = await fetchWithAuth(`${API_BASE}/articles/search?${params.toString()}`);
    return handleResponse<KBArticle[]>(response);
  },

  async getPopularArticles(limit: number = 10): Promise<KBArticle[]> {
    const response = await fetchWithAuth(`${API_BASE}/articles/popular?limit=${limit}`);
    return handleResponse<KBArticle[]>(response);
  },

  async getRecentArticles(limit: number = 10): Promise<KBArticle[]> {
    const response = await fetchWithAuth(`${API_BASE}/articles/recent?limit=${limit}`);
    return handleResponse<KBArticle[]>(response);
  },

  async markHelpful(id: string): Promise<{ helpfulCount: number }> {
    const response = await fetchWithAuth(`${API_BASE}/articles/${id}/helpful`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  async markNotHelpful(id: string): Promise<{ notHelpfulCount: number }> {
    const response = await fetchWithAuth(`${API_BASE}/articles/${id}/not-helpful`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  // ============ STATS & TAGS ============

  async getStats(): Promise<KBStats> {
    const response = await fetchWithAuth(`${ADMIN_API_BASE}/stats`);
    return handleResponse<KBStats>(response);
  },

  async getAllTags(): Promise<string[]> {
    const response = await fetchWithAuth(`${API_BASE}/tags`);
    return handleResponse<string[]>(response);
  },
};
